import { zodResolver } from '@hookform/resolvers/zod';
import {
  Xero,
  XeroCoaListResponse,
  XeroCompaniesListResponse,
  XeroFormSchema,
  XeroFormType,
  XeroUserInfoResponse,
} from '@monetize/types/app';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdRefresh } from 'react-icons/md';
import {
  useSetXeroCompany,
  useUpdateXeroConnection,
} from '../../../../../api/accountingService';
import {
  MButton,
  MDivider,
  MFormField,
  MGrid,
  MGridItem,
  MHStack,
  MIconButton,
  MText,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { logger } from '../../../../../services/logger';
import { XERO_FORM_FIELDS } from './accounting.constants';

interface XeroFormProps {
  connection: Xero;
  userInfo?: XeroUserInfoResponse;
  coaData?: XeroCoaListResponse;
  isRefetchingCoa?: boolean;
  onClose: () => void;
  refetchCoaData?: () => void;
  companiesData?: XeroCompaniesListResponse;
  isLoadingCompanies?: boolean;
}

export const XeroForm = ({
  connection,
  userInfo,
  coaData,
  isRefetchingCoa,
  onClose,
  refetchCoaData,
  companiesData,
  isLoadingCompanies,
}: XeroFormProps) => {
  const updateXeroConnection = useUpdateXeroConnection({
    meta: { showErrorToast: true },
  });

  const setXeroCompany = useSetXeroCompany({
    meta: { showErrorToast: true },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<XeroFormType>({
    resolver: zodResolver(XeroFormSchema),
    mode: 'onChange',
    defaultValues: {
      companyId: connection.companyId?.value || '',
      defaultPaymentCoa: connection.defaultPaymentCoa,
      defaultProductCoa: connection.defaultProductCoa,
      name: userInfo?.name,
      email: userInfo?.email,
      userId: userInfo?.userId,
      userName: userInfo?.username,
    },
  });

  const paymentCoaOptions = useMemo(() => {
    return [
      ...(coaData?.paymentCoas?.map((coa) => ({
        value: coa.code,
        title: `${coa.code} - ${coa.name}`,
      })) || []),
    ];
  }, [coaData?.paymentCoas]);

  const productCoaOptions = useMemo(() => {
    return [
      ...(coaData?.productCoas?.map((coa) => ({
        value: coa.code,
        title: `${coa.code} - ${coa.name}`,
      })) || []),
    ];
  }, [coaData?.productCoas]);

  const companyOptions = useMemo(() => {
    return [
      ...(companiesData?.companies?.map((company) => ({
        value: company.id,
        title: company.name,
      })) || []),
    ];
  }, [companiesData]);

  const handleCompanyChange = async (
    selectedCompanyId: string,
    fieldOnChange: (value: string) => void,
  ) => {
    fieldOnChange(selectedCompanyId);

    if (
      selectedCompanyId &&
      selectedCompanyId !== connection.companyId?.value
    ) {
      try {
        await setXeroCompany.mutateAsync({
          companyId: selectedCompanyId,
        });
      } catch (error) {
        logger.error('Failed to set Xero company', error);
      }
    }
  };

  const onSubmitHandler = async (data: XeroFormType) => {
    const payload = {
      defaultPaymentCoa: data.defaultPaymentCoa,
      defaultProductCoa: data.defaultProductCoa,
    };

    await updateXeroConnection.mutateAsync(payload);
    onClose();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmitHandler, (error) => logger.error(error))}
      style={{ width: '100%' }}
    >
      <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
        <MGridItem colSpan={12}>
          <MFormField
            error={errors.companyId}
            label={XERO_FORM_FIELDS.companyId.label}
            tooltip={XERO_FORM_FIELDS.companyId.tooltip}
            isRequired
          >
            <Controller
              name="companyId"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  {...field}
                  placeholder="Select Xero Company"
                  items={companyOptions}
                  value={field.value || ''}
                  onChange={(selectedCompanyId) =>
                    handleCompanyChange(selectedCompanyId, field.onChange)
                  }
                  isDisabled={
                    setXeroCompany.isPending ||
                    isLoadingCompanies ||
                    !!connection.companyId?.value
                  }
                  loading={isLoadingCompanies}
                />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.defaultPaymentCoa}
            label={XERO_FORM_FIELDS.defaultPaymentCoa.label}
            tooltip={XERO_FORM_FIELDS.defaultPaymentCoa.tooltip}
            labelRightComponent={
              refetchCoaData ? (
                <MIconButton
                  aria-label="Refresh COA data"
                  icon={<MdRefresh />}
                  fontSize={18}
                  size="sm"
                  variant="ghost"
                  onClick={refetchCoaData}
                  isLoading={isRefetchingCoa}
                  ml={1}
                  mt={-2}
                />
              ) : undefined
            }
          >
            <Controller
              name="defaultPaymentCoa"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  {...field}
                  placeholder="Select Payment COA"
                  items={paymentCoaOptions}
                  value={field.value?.code || ''}
                  onChange={(selectedCode) => {
                    if (selectedCode === '') {
                      field.onChange(null);
                    } else {
                      const selectedCoa = coaData?.paymentCoas?.find(
                        (coa) => coa.code === selectedCode,
                      );
                      if (selectedCoa) {
                        field.onChange({
                          code: selectedCoa.code,
                          name: selectedCoa.name,
                        });
                      }
                    }
                  }}
                />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.defaultProductCoa}
            label={XERO_FORM_FIELDS.defaultProductCoa.label}
            tooltip={XERO_FORM_FIELDS.defaultProductCoa.tooltip}
            labelRightComponent={
              refetchCoaData ? (
                <MIconButton
                  aria-label="Refresh COA data"
                  icon={<MdRefresh />}
                  fontSize={18}
                  size="sm"
                  variant="ghost"
                  onClick={refetchCoaData}
                  isLoading={isRefetchingCoa}
                  ml={1}
                  mt={-2}
                />
              ) : undefined
            }
          >
            <Controller
              name="defaultProductCoa"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  {...field}
                  placeholder="Select Product COA"
                  items={productCoaOptions}
                  value={field.value?.code || ''}
                  onChange={(selectedCode) => {
                    if (selectedCode === '') {
                      field.onChange(null);
                    } else {
                      const selectedCoa = coaData?.productCoas?.find(
                        (coa) => coa.code === selectedCode,
                      );
                      if (selectedCoa) {
                        field.onChange({
                          code: selectedCoa.code,
                          name: selectedCoa.name,
                        });
                      }
                    }
                  }}
                />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField label={XERO_FORM_FIELDS.name.label} isReadOnly>
            <Controller
              name="name"
              control={control}
              render={({ field: { value } }) => <MText>{value}</MText>}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField label={XERO_FORM_FIELDS.email.label} isReadOnly>
            <Controller
              name="email"
              control={control}
              render={({ field: { value } }) => <MText>{value}</MText>}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField label={XERO_FORM_FIELDS.userName.label} isReadOnly>
            <Controller
              name="userName"
              control={control}
              render={({ field: { value } }) => <MText>{value}</MText>}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField label={XERO_FORM_FIELDS.userId.label} isReadOnly>
            <Controller
              name="userId"
              control={control}
              render={({ field: { value } }) => <MText>{value}</MText>}
            />
          </MFormField>
        </MGridItem>
      </MGrid>
      <MDivider my={4} />
      <MHStack gap="4" justify="end" align="center">
        <MButton variant="cancel" onClick={onClose}>
          Cancel
        </MButton>
        <MButton
          type="submit"
          variant="primary"
          isLoading={updateXeroConnection.isPending}
          isDisabled={
            updateXeroConnection.isPending ||
            setXeroCompany.isPending ||
            !isDirty ||
            !control._formValues.companyId
          }
        >
          Save
        </MButton>
      </MHStack>
    </form>
  );
};
